// src/store/blog.js
import { API_URL } from "@/mainconfig"; // axios instance kamu
// Kalau API_URL bukan axios instance global, bisa ganti pakai `import axios from "axios";`

const TTL_MS = 5 * 60 * 1000; // 5 menit
let inFlight = null; // guard agar tidak double fetch

const state = {
    loading: false,
    error: null,
    errorMessage: null,
    items: [], // list 3 post terakhir
    lastFetchedAt: null,
};

const getters = {
    isLoading: (s) => s.loading,
    getBlogError: (s) => s.errorMessage,
    items: (s) => s.items,
    latestPosts: (s) => s.items, // alias biar enak dipakai di component
    isLoaded: (s) => (s.items || []).length > 0,
    isStale: (s) => !s.lastFetchedAt || Date.now() - s.lastFetchedAt > TTL_MS,
};

const actions = {
    /**
     * Ambil 3 postingan terbaru dari WordPress
     * Pakai cache + TTL + inFlight guard
     */
    async fetchLatest({ state, commit, getters }, options = {}) {
        const { force = false } = options;

        // Pakai cache kalau masih fresh
        if (!force && getters.isLoaded && !getters.isStale) {
            return { code: 0, data: state.items };
        }

        // Kalau sudah ada request jalan, pakai promise yg sama
        if (inFlight) return inFlight;

        commit("setLoading", true);

        inFlight = (async () => {
            try {
                const res = await API_URL.get(
                    "https://www.cloudhomes.id/blog/wp-json/wp/v2/posts",
                    {
                        params: {
                            per_page: 3, // cuma 3 post terakhir
                            orderby: "date",
                            order: "desc",
                            _embed: true, // supaya bisa ambil featured image
                        },
                    }
                );

                const payload = res?.data;

                if (!Array.isArray(payload)) {
                    throw new Error("Invalid response shape from WordPress.");
                }

                const mapped = payload.map(mapItem);

                commit("setItems", mapped);
                commit("setError", null);
                commit("setLastFetchedAt", Date.now());

                return { code: 0, data: mapped };
            } catch (error) {
                const message =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Failed to fetch blog posts.";
                commit("setError", message);
                throw error;
            } finally {
                commit("setLoading", false);
                inFlight = null;
            }
        })();

        return inFlight;
    },

    /**
     * Pastikan data sudah ada (misal dipanggil di mounted halaman)
     */
    async ensureLatest({ getters, dispatch, state }) {
        if (getters.isLoaded && !getters.isStale) return state.items;
        await dispatch("fetchLatest");
        return state.items || [];
    },
};

const mutations = {
    setLoading(s, v) {
        s.loading = v;
    },
    setError(s, e) {
        s.error = e;
        s.errorMessage = e || "An error occurred.";
    },
    setItems(s, items) {
        s.items = items || [];
    },
    setLastFetchedAt(s, ts) {
        s.lastFetchedAt = ts ?? Date.now();
    },
    clear(s) {
        s.items = [];
        s.lastFetchedAt = null;
        s.error = null;
        s.errorMessage = null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

/* ============ mapper util ============ */

function mapItem(raw) {
    const embeddedMedia = raw?._embedded?.["wp:featuredmedia"]?.[0];

    const featuredImageUrl =
        embeddedMedia?.source_url ||
        embeddedMedia?.media_details?.sizes?.medium?.source_url ||
        embeddedMedia?.media_details?.sizes?.full?.source_url ||
        "";

    return {
        id: raw?.id,
        date: raw?.date || "",
        slug: raw?.slug || "",
        title: raw?.title?.rendered || "",
        // biasanya dipakai dengan v-html
        excerpt: raw?.excerpt?.rendered || "",
        link: raw?.link || "",
        featuredMediaId: raw?.featured_media || null,
        featuredImageUrl,
        categories: raw?.categories || [],
        tags: raw?.tags || [],
    };
}
